import { ArrowUpDown, Check, LayoutGrid, ListFilter } from "lucide-react";
import { Fragment, useRef } from "react";

import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { cn } from "@/lib/utils.ts";
import {
  ARTIFACT_LIBRARY_SHARING_OPTIONS,
  type ArtifactLibrarySharingId,
  DEFAULT_LIBRARY_SORT,
  LIBRARY_SORTS,
  type LibrarySort,
} from "@/types/artifactLibrary.ts";

/**
 * One option in any dimension. `value` is always a string: callers whose domain
 * isn't (Contacts' numeric connector id) encode at the edge, which is what they
 * already do to round-trip through the URL, and which makes every dimension
 * serializable for free.
 */
export type PanelControlOption = {
  value: string;
  label: string;
  /**
   * How many items the option would show, when the page counts them. A segment
   * row draws it after the label; the menu does not.
   */
  count?: number;
};

/**
 * A single selectable axis. There is always a value — "All" is an option, never
 * an absent value, which is what let four different validation styles grow up
 * around the same question.
 */
export type PanelControlDimension = {
  /**
   * The axis's name, used as the menu group heading. One word per axis, so the
   * same concept can't be "Filter" on one page and "Type" on the next.
   */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Rendered in exactly this order, on every surface. */
  options: readonly PanelControlOption[];
  /**
   * The resting value. Anything else lights the applied treatment, replacing
   * the per-page `!== "all"` literals.
   */
  defaultValue: string;
  /**
   * What this axis does to the list. A filter narrows it, a sort reorders it, a
   * view redraws it. Only a filter can be "applied", so only a filter tints the
   * trigger or contributes a segment to its label; and only a sort earns the
   * "& Sort" in the resting label, which is why this is a role rather than the
   * single boolean it replaced — that flag could not tell a sort from a view,
   * and a page with a view but no sort would have advertised sorting.
   *
   * Defaults to "filter": the axes that narrow are the common case.
   */
  role?: "filter" | "sort" | "view";
  /**
   * Where the group sits in the menu: lower first, default 0. Ties keep the
   * filter → sort → view sequence, so only an axis that sets this moves.
   */
  order?: number;
  /**
   * Whether this axis is spelled out as a row of segments above the list, or
   * stays a group inside the menu. Defaults to the menu.
   *
   * Opt-in per axis rather than derived from `options.length`, because the
   * length is not what decides it: Contacts' Account axis can hold two options
   * and still belong in the menu, since each is an email address and two of
   * them fill the row. The page knows what its labels are; a threshold here
   * would only guess.
   *
   * Only a filter is offered the choice. A segment row answers "what am I
   * looking at", which is the question a filter asks — a sort or a view says
   * nothing about what the list holds, and promoting one would spend the row's
   * width advertising the order of the results.
   */
  display?: "segmented" | "menu";
  /**
   * Set on an axis where the user picks several options at once: the picked
   * ones, none meaning the default option. `onChange` then gets the option the
   * user toggled, the menu stays open between picks, and the axis is applied
   * while any option is picked, whatever `value` says.
   */
  values?: readonly string[];
  statsigName: string;
};

/** Whether the axis shows `optionValue` as chosen. */
export const isOptionSelected = (axis: PanelControlDimension, optionValue: string): boolean => {
  if (!axis.values) return axis.value === optionValue;
  return axis.values.length === 0
    ? optionValue === axis.defaultValue
    : axis.values.includes(optionValue);
};

/** Whether the axis has moved off its resting choice. */
const isAxisApplied = (axis: PanelControlDimension): boolean =>
  axis.values ? axis.values.length > 0 : axis.value !== axis.defaultValue;

/** The labels of the options the axis has chosen, in option order. */
const chosenLabels = (axis: PanelControlDimension): string[] =>
  axis.options.filter((option) => isOptionSelected(axis, option.value)).map((o) => o.label);

const isFilter = (axis: PanelControlDimension) => (axis.role ?? "filter") === "filter";

/**
 * How a panel draws its list. "card" rather than "grid": the thread list has
 * called this shape a card since `ThreadActionSurface`, and one word for one
 * thing is the whole point of this module.
 */
export type PanelViewMode = "list" | "card";

export const VIEW_OPTIONS: readonly PanelControlOption[] = [
  { value: "list", label: "List view" },
  { value: "card", label: "Card view" },
];

export const isPanelViewMode = (value: string): value is PanelViewMode =>
  VIEW_OPTIONS.some((option) => option.value === value);

/**
 * A panel's view axis, built from its state. Every panel that offers the choice
 * spells it the same way, so only the default differs: galleries open on cards,
 * the reading lists (Library, threads) open on rows.
 */
export const viewAxis = ({
  value,
  onChange,
  defaultValue,
  statsigName,
}: {
  value: PanelViewMode;
  onChange: (mode: PanelViewMode) => void;
  defaultValue: PanelViewMode;
  statsigName: string;
}): PanelControlDimension => ({
  label: "View",
  value,
  onChange: (next) => {
    if (isPanelViewMode(next)) onChange(next);
  },
  options: VIEW_OPTIONS,
  defaultValue,
  statsigName,
});

const isSharingId = (value: string): value is ArtifactLibrarySharingId =>
  ARTIFACT_LIBRARY_SHARING_OPTIONS.some((option) => option.id === value);

/**
 * The Library's Sharing axis, built from its host's state. Both hosts — the
 * Settings tab and the search dialog — spell it through here, so the menu group
 * reads, sits, and reports the same on each.
 */
export const sharingAxis = ({
  value,
  onChange,
}: {
  value: ArtifactLibrarySharingId;
  onChange: (sharing: ArtifactLibrarySharingId) => void;
}): PanelControlDimension => ({
  label: "Sharing",
  value,
  onChange: (next) => {
    if (isSharingId(next)) onChange(next);
  },
  options: ARTIFACT_LIBRARY_SHARING_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label,
  })),
  defaultValue: "all",
  // Ends the menu, below View: the secondary choice on both surfaces.
  order: 1,
  statsigName: "artifact-library-sharing",
});

/**
 * The sort axis's options. "Newest" rather than "Date": it names the order the
 * user gets, where "Date" only names the key and leaves the direction to be
 * guessed.
 */
const LIBRARY_SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "date", label: "Newest" },
] as const satisfies ReadonlyArray<{ value: LibrarySort; label: string }>;

const isLibrarySort = (value: string): value is LibrarySort =>
  (LIBRARY_SORTS as readonly string[]).includes(value);

/**
 * The Library's Sort axis, built from its host's state.
 *
 * Here beside {@link sharingAxis} for the same reason: every surface that lists
 * the Library spells the axis through one definition, so the group cannot come
 * to read or report differently on one of them. It is what the body offers when
 * it owns its own controls and what a host puts in the toolbar menu when it
 * owns them instead.
 */
export const librarySortAxis = ({
  value,
  onChange,
}: {
  value: LibrarySort;
  onChange: (sort: LibrarySort) => void;
}): PanelControlDimension => ({
  label: "Sort",
  value,
  onChange: (next) => {
    if (isLibrarySort(next)) onChange(next);
  },
  options: LIBRARY_SORT_OPTIONS,
  defaultValue: DEFAULT_LIBRARY_SORT,
  statsigName: "artifact-library-sort",
});

/**
 * Everything one panel's menu offers. A page holds this as a single object and
 * hands the same one to `PanelControls` and to `appliedFilterLabel`, so the menu
 * and the header measurement can never be computed from different axes.
 */
export type PanelControlAxes = {
  filters?: readonly PanelControlDimension[];
  sort?: PanelControlDimension;
  /**
   * The card/list choice, when the panel offers one. After the filters and sort
   * unless an axis sets `order`: it is about how the list is drawn, not what it
   * holds.
   */
  view?: PanelControlDimension;
  /**
   * Whether the trigger keeps its resting label and adds how many filters
   * narrow the list, rather than naming their values. For a page whose filters
   * take several values each, where the names would not fit a button.
   */
  countApplied?: boolean;
};

/**
 * The axes in menu order, each with its role stamped on. Roles are set here
 * rather than asked of callers: a page that forgot one on its sort or view
 * would tint its own trigger for reordering or for choosing cards, and nothing
 * would catch it. The sort by `order` is stable, so unordered axes keep the
 * filter → sort → view sequence.
 */
const panelAxes = ({ filters, sort, view }: PanelControlAxes): PanelControlDimension[] =>
  [
    ...(filters ?? []).map((axis) => ({ ...axis, role: "filter" as const })),
    ...(sort ? [{ ...sort, role: "sort" as const }] : []),
    ...(view ? [{ ...view, role: "view" as const }] : []),
  ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/**
 * A panel's axes split by where they are shown: the filters spelled out as
 * segment rows above the list, and everything left for the menu behind the
 * filter glyph.
 *
 * One function rather than two filters at each call site, so a toolbar and the
 * menu beside it can never disagree about which axes each is responsible for —
 * an axis shown in both places would offer two controls for one value, and one
 * shown in neither would strand a narrowed list with nothing to widen it.
 *
 * The menu half keeps its `PanelControlAxes` shape so it can be spread straight
 * into `PanelControls`, which then behaves exactly as it does on a page with no
 * segment row at all.
 */
export const splitPanelControls = (
  controls: PanelControlAxes,
): { segmented: PanelControlDimension[]; menu: PanelControlAxes } => {
  const filters = controls.filters ?? [];
  const segmented = filters.filter((axis) => axis.display === "segmented");
  const menuFilters = filters.filter((axis) => axis.display !== "segmented");
  return {
    segmented,
    menu: {
      ...controls,
      // Undefined rather than an empty array: `PanelControls` reads a missing
      // `filters` as "this panel has none", which is what a page whose every
      // filter is on the segment row actually is.
      filters: menuFilters.length > 0 ? menuFilters : undefined,
    },
  };
};

/**
 * Whether the menu half still has anything to show. A page that promoted its
 * only filter and offers no sort or view has an empty menu, and a glyph that
 * opens onto nothing is worse than no glyph.
 */
export const hasMenuControls = (menu: PanelControlAxes) =>
  Boolean(menu.filters?.length || menu.sort || menu.view);

/**
 * What the trigger reads while nothing narrows the list. It names what the menu
 * actually offers, so a panel with no sort axis doesn't advertise sorting —
 * and one that gains sorting later starts saying so without an edit here.
 */
const restingLabel = (axes: readonly PanelControlDimension[]): string => {
  const names: string[] = [];
  if (axes.some(isFilter)) names.push("Filter");
  if (axes.some((axis) => axis.role === "sort")) names.push("Sort");
  // Named only when it is alone. A panel offering filters has a menu about what
  // the list holds, and appending the way it is drawn to that trigger spends
  // the width on the least of the three.
  if (names.length === 0 && axes.some((axis) => axis.role === "view")) names.push("View");
  return names.join(" & ");
};

/**
 * Whether anything is currently narrowing the list.
 *
 * Only filters count. A sort says nothing about what the list is showing, only
 * about the order it is in, and a view only about how it is drawn — which is why
 * changing either leaves a page's description in place while picking a filter
 * takes it away.
 *
 * The same test that decides whether the menu trigger reads as applied, so a
 * page cannot end up tinting its filter glyph while still introducing itself as
 * though it were showing everything.
 */
export const hasAppliedFilters = (controls: PanelControlAxes) =>
  appliedFilterAxes(controls).length > 0;

/** The filters currently narrowing the list, in menu order. */
const appliedFilterAxes = (controls: PanelControlAxes): PanelControlDimension[] =>
  panelAxes(controls).filter((axis) => isFilter(axis) && isAxisApplied(axis));

/**
 * The glyph and accessible name for the trigger, chosen from what the menu
 * actually holds.
 *
 * A page may promote its filters to a segment row and leave only a sort or a
 * view behind, and a `ListFilter` over that menu tells the user the control
 * narrows the list when it reorders or redraws it. The same reasoning
 * {@link restingLabel} follows: name what is offered, not what this control
 * usually offers.
 */
const triggerIntent = (
  axes: readonly PanelControlDimension[],
  subject: string,
): { Icon: typeof ListFilter; label: string } => {
  if (axes.some(isFilter)) return { Icon: ListFilter, label: `Filter ${subject}` };
  if (axes.some((axis) => axis.role === "sort"))
    return { Icon: ArrowUpDown, label: `Sort ${subject}` };
  // Not built from `subject`: the others read as a verb on a plural noun, and
  // the subjects are not reliably plural ("library"), which would make this the
  // one label that reads wrong.
  return { Icon: LayoutGrid, label: "Change the view" };
};

/**
 * What the trigger says: the applied values, or the resting label when none
 * narrow the list. Several reads as "Me / work@example.com", in axis
 * order.
 *
 * Exported because the panel header measures its tool cluster to decide when to
 * collapse to icons, and this string is the cluster's width — a page passes it
 * as `toolsKey` so a label change re-runs that measurement. Only filters
 * contribute a segment: a sort says nothing about what the list is showing,
 * only about the order it is in, and a view only about how it is drawn.
 */
export const appliedFilterLabel = (controls: PanelControlAxes): string => {
  const axes = panelAxes(controls);
  const appliedAxes = appliedFilterAxes(controls);
  if (controls.countApplied) {
    const resting = restingLabel(axes);
    return appliedAxes.length > 0 ? `${resting} ${appliedAxes.length}` : resting;
  }
  const applied = appliedAxes
    .map((axis) => chosenLabels(axis).join(", "))
    .filter((label) => label !== "");
  return applied.length > 0 ? applied.join(" / ") : restingLabel(axes);
};

/**
 * The panel's whole control cluster: filter axes, an optional sort, and an
 * optional view, in one menu behind one trigger.
 *
 * Pages supply options and values, never layout beyond an axis's `order`. A
 * page without an axis simply omits it — Skills passes no filters, Memories no
 * view — so no page needs to know how the others are arranged, and adding an
 * axis can't rearrange anyone else's header.
 *
 * Every axis renders the same way — a labelled group of check-column rows — so
 * separators, ordering, and alignment cannot diverge between them, and an axis
 * added later inherits the whole treatment.
 */
export const PanelControls = ({
  filters,
  sort,
  view,
  countApplied,
  subject,
  labeled = false,
  statsigName,
  triggerClassName,
}: PanelControlAxes & {
  /**
   * What this panel lists, e.g. "routines". The control composes the accessible
   * name from it — "Filter routines", or "Sort routines" for a menu whose
   * filters were promoted to a segment row — so a screen reader hears what is
   * being acted on while the phrasing stays identical across panels.
   */
  subject: string;
  /**
   * The full header row shows the labeled trigger; the compact row and mobile
   * nav bar show the icon. One component renders both so the menu can't drift
   * between them.
   */
  labeled?: boolean;
  /** Base analytics name for the trigger, e.g. "contacts-filter". */
  statsigName: string;
  /**
   * Merged onto the icon-only trigger, for a host that dresses its controls —
   * a page's action row passes `panelToolbarControlClass` so this reads at the
   * same 28px box and 16px glyph as the segments beside it.
   * The settings chrome passes nothing and keeps the default.
   */
  triggerClassName?: string;
}) => {
  const axes = panelAxes({ filters, sort, view });
  const label = appliedFilterLabel({ filters, sort, view });
  const isApplied = hasAppliedFilters({ filters, sort, view });
  const appliedCount = countApplied ? appliedFilterAxes({ filters, sort, view }).length : 0;
  const { Icon, label: intentLabel } = triggerIntent(axes, subject);
  const triggerLabel = appliedCount > 0 ? `${intentLabel} (${appliedCount} applied)` : intentLabel;
  // Where the user was when they reached for this menu. Radix hands focus back
  // to the trigger on close, which is right when the trigger is where they came
  // from and wrong when they were typing: narrowing a list from a focused search
  // field would drop the cursor out of it, and the next keystroke would go
  // nowhere.
  const origin = useRef<HTMLElement | null>(null);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        // Read on pointerdown, which runs before the browser moves focus to the
        // trigger. Opening from the keyboard leaves this null — there focus was
        // already on the trigger, so Radix's own behaviour is the correct one.
        onPointerDownCapture={() => {
          origin.current = document.activeElement as HTMLElement | null;
        }}
      >
        {labeled ? (
          <Button
            variant="outline"
            className={cn("h-9 shrink-0", isApplied && "border-primary text-primary")}
            aria-label={triggerLabel}
            data-statsig-name={statsigName}
          >
            <Icon className="size-4 shrink-0" />
            {countApplied ? restingLabel(axes) : label}
            {appliedCount > 0 ? <AppliedCount count={appliedCount} /> : null}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "shrink-0",
              isApplied && "border-primary",
              appliedCount > 0 && "relative",
              triggerClassName,
            )}
            aria-label={triggerLabel}
            data-statsig-name={statsigName}
          >
            <Icon className={cn("size-5", isApplied && "text-primary")} />
            {appliedCount > 0 ? (
              <AppliedCount count={appliedCount} className="absolute -top-1 -right-1" />
            ) : null}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onCloseAutoFocus={(event) => {
          const target = origin.current;
          origin.current = null;
          // `isConnected` guards a field that closed while the menu was open —
          // focusing a detached node silently sends focus to the body instead,
          // which is worse than the trigger Radix would have picked.
          if (!target || target === document.body || !target.isConnected) return;
          event.preventDefault();
          target.focus();
        }}
      >
        {axes.map((axis, index) => (
          // A Fragment, not a wrapper element: Radix reads the menu's items as
          // direct children for roving focus and typeahead, so a div here would
          // quietly break keyboard navigation. Separators go between groups
          // only; a leading one reads as a divider under the trigger.
          <Fragment key={axis.statsigName}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <PanelDimensionGroup axis={axis} />
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** How many filters narrow the list, on the trigger of a panel that counts them. */
const AppliedCount = ({ count, className }: { count: number; className?: string }) => (
  <Badge
    size="xs"
    // The trigger's own name says how many apply; this is the same number for the eye.
    aria-hidden
    className={cn("min-w-4 justify-center font-medium", className)}
  >
    {count}
  </Badge>
);

/**
 * One axis as a labelled group of check-column rows.
 *
 * Exported for a page whose menu is its own rather than `PanelControls` — Tasks
 * keeps its view and, on a phone, its filters behind the sliders beside the
 * settings they sit with — so those rows draw exactly as these do.
 */
export const PanelDimensionGroup = ({ axis }: { axis: PanelControlDimension }) => (
  <>
    <DropdownMenuLabel>{axis.label}</DropdownMenuLabel>
    {axis.options.map((option) => (
      <DropdownMenuItem
        key={option.value}
        className="justify-between"
        // An axis of several picks stays open, so picking a second option is
        // one click rather than a trip back through the trigger.
        onSelect={axis.values ? (event) => event.preventDefault() : undefined}
        // Several can be checked, so each says whether it is, as a checkbox
        // would; a single-choice row keeps the menu's own semantics.
        {...(axis.values
          ? { role: "menuitemcheckbox", "aria-checked": isOptionSelected(axis, option.value) }
          : {})}
        onClick={() => axis.onChange(option.value)}
        data-statsig-name={`${axis.statsigName}-${option.value}`}
      >
        {/* The label leads, flush with the group's heading and every other row
            in the menu, and the check sits at the end as the row's state. It
            keeps its place on every row, drawn invisible, so the menu's width
            does not change as the selection moves. */}
        {option.label}
        <Check className={cn("size-4", !isOptionSelected(axis, option.value) && "invisible")} />
      </DropdownMenuItem>
    ))}
  </>
);
